import {
  Children,
  isValidElement,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type SelectHTMLAttributes,
} from "react";
import { cn } from "./utils";

type AdminControlTone = "default" | "warning" | "info";

interface AdminSelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "children" | "defaultValue" | "multiple" | "onChange" | "size" | "value"> {
  children: ReactNode;
  value: string | number;
  onValueChange: (value: string) => void;
  tone?: AdminControlTone;
}

interface AdminSelectOption {
  disabled: boolean;
  label: ReactNode;
  value: string;
}

const toneClass: Record<AdminControlTone, string> = {
  default: "border-slate-200 focus:border-blue-500 focus:ring-blue-500",
  warning: "border-amber-200 focus:border-amber-500 focus:ring-amber-500",
  info: "border-blue-200 focus:border-blue-500 focus:ring-blue-500",
};

const optionText = (value: ReactNode): string => {
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  return "";
};

const AdminSelect = ({
  value,
  onValueChange,
  tone = "default",
  className,
  children,
  disabled = false,
  id,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  "aria-invalid": ariaInvalid,
}: AdminSelectProps) => {
  const generatedId = useId();
  const controlId = id ?? `admin-select-${generatedId}`;
  const listboxId = `${controlId}-listbox`;
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const options = useMemo<AdminSelectOption[]>(
    () =>
      Children.toArray(children).flatMap((child) => {
        if (!isValidElement<{ children?: ReactNode; disabled?: boolean; value?: string | number }>(child) || child.type !== "option") {
          return [];
        }
        const optionValue = child.props.value == null ? optionText(child.props.children) : String(child.props.value);
        return [{ disabled: Boolean(child.props.disabled), label: child.props.children, value: optionValue }];
      }),
    [children],
  );

  const selectedIndex = Math.max(0, options.findIndex((option) => option.value === String(value)));
  const selectedOption = options[selectedIndex];

  useEffect(() => {
    if (!open) {
      return;
    }
    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  useEffect(() => {
    if (open) {
      setActiveIndex(selectedIndex);
    }
  }, [open, selectedIndex]);

  const moveActive = (direction: 1 | -1) => {
    if (options.length === 0) {
      return;
    }
    let nextIndex = activeIndex;
    do {
      nextIndex = (nextIndex + direction + options.length) % options.length;
    } while (options[nextIndex]?.disabled && nextIndex !== activeIndex);
    setActiveIndex(nextIndex);
  };

  const selectOption = (option: AdminSelectOption) => {
    if (option.disabled) {
      return;
    }
    onValueChange(option.value);
    setOpen(false);
    triggerRef.current?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) {
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) {
        setOpen(true);
      } else {
        moveActive(event.key === "ArrowDown" ? 1 : -1);
      }
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (!open) {
        setOpen(true);
      } else {
        const option = options[activeIndex];
        if (option) {
          selectOption(option);
        }
      }
      return;
    }
    if (event.key === "Escape" && open) {
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    }
  };

  return (
    <div ref={rootRef} className={cn("relative w-full", className)} onKeyDown={handleKeyDown}>
      <button
        ref={triggerRef}
        id={controlId}
        type="button"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-invalid={ariaInvalid}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-expanded={open}
        aria-activedescendant={open ? `${listboxId}-option-${activeIndex}` : undefined}
        onClick={() => setOpen((current) => !current)}
        className={cn(
          "flex h-9 w-full items-center justify-between gap-2 rounded-lg border bg-white px-2.5 text-left text-xs text-slate-700 outline-none transition-colors focus:ring-1 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400",
          toneClass[tone],
        )}
      >
        <span className="min-w-0 flex-1 truncate">{selectedOption?.label ?? "선택"}</span>
        <i
          aria-hidden="true"
          className={cn(
            "ri-arrow-down-s-line shrink-0 text-base text-slate-400 transition-transform",
            open && "rotate-180 text-slate-600",
          )}
        />
      </button>

      {open && (
        <div
          id={listboxId}
          role="listbox"
          aria-labelledby={ariaLabelledBy ?? controlId}
          className="absolute left-0 right-0 z-50 mt-1 max-h-60 overflow-y-auto rounded-lg border border-slate-200 bg-white p-1 shadow-lg"
        >
          {options.map((option, index) => {
            const selected = option.value === String(value);
            const active = index === activeIndex;
            return (
              <button
                key={`${option.value}-${index}`}
                id={`${listboxId}-option-${index}`}
                type="button"
                role="option"
                aria-selected={selected}
                disabled={option.disabled}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => selectOption(option)}
                className={cn(
                  "flex min-h-8 w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-xs text-slate-700 outline-none",
                  active && "bg-slate-100",
                  selected && "bg-blue-50 font-semibold text-blue-700",
                  option.disabled && "cursor-not-allowed opacity-40",
                )}
              >
                <i className={cn("ri-check-line text-sm", selected ? "visible" : "invisible")} aria-hidden="true" />
                <span className="min-w-0 flex-1 truncate">{option.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminSelect;
